import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_ALL_ORDERS } from '../graphql/queries'
import type {
  AllOrdersFilters,
  AllOrdersPage,
  AllOrdersSortField,
  AllOrdersSource,
  Order,
  OrderStatus,
  OrderType,
  SortDirection,
} from '../types/order'

export const ALL_ORDERS_PAGE_SIZE = 50

const SEARCH_DEBOUNCE_MS = 400

// UI-side filter state. Raw input values live here; buildAllOrdersFilters
// converts them to the API's AllOrdersFilters shape.
export interface AllOrdersFilterState {
  dateFrom: string // 'YYYY-MM-DD' or '' (native date input value)
  dateTo: string
  statuses: OrderStatus[] // paid statuses only; [] = all (API then excludes UNPAID)
  orderType: OrderType | '' // '' = all
  sources: AllOrdersSource[] // [] = all
  minTotal: string // raw EUR text ("12,50" accepted)
  maxTotal: string
  hasDiscount: 'both' | 'only' | 'none'
  search: string // raw input value (debounced before hitting the API)
}

export const EMPTY_ALL_ORDERS_FILTERS: AllOrdersFilterState = {
  dateFrom: '',
  dateTo: '',
  statuses: [],
  orderType: '',
  sources: [],
  minTotal: '',
  maxTotal: '',
  hasDiscount: 'both',
  search: '',
}

// Raw EUR text → cents, or undefined when empty/invalid. Accepts a comma
// decimal separator ("12,50").
function eurToCents(value: string): number | undefined {
  if (!value.trim()) return undefined
  const parsed = parseFloat(value.trim().replace(',', '.'))
  if (Number.isNaN(parsed) || parsed < 0) return undefined
  return Math.round(parsed * 100)
}

// Pure state → API filters mapping, exported so future report pages can drive
// GET_ALL_ORDERS with their own state management.
export function buildAllOrdersFilters(
  state: AllOrdersFilterState,
  unpaidView: boolean,
  debouncedSearch: string
): AllOrdersFilters {
  const filters: AllOrdersFilters = {}

  // Inclusive local-time day bounds — accounting cross-checks care about the
  // exact day edges in restaurant-local time.
  if (state.dateFrom) filters.date_from = new Date(`${state.dateFrom}T00:00:00`).toISOString()
  if (state.dateTo) filters.date_to = new Date(`${state.dateTo}T23:59:59.999`).toISOString()

  // The unpaid research view always sends [UNPAID] alone and drops any paid
  // selection — the API rejects UNPAID mixed with other statuses.
  if (unpaidView) {
    filters.statuses = ['UNPAID']
  } else if (state.statuses.length > 0) {
    filters.statuses = state.statuses
  }

  if (state.orderType) filters.order_type = state.orderType
  if (state.sources.length > 0) filters.sources = state.sources

  const minCents = eurToCents(state.minTotal)
  const maxCents = eurToCents(state.maxTotal)
  if (minCents !== undefined) filters.min_total_cents = minCents
  if (maxCents !== undefined) filters.max_total_cents = maxCents

  if (state.hasDiscount === 'only') filters.has_discount = true
  if (state.hasDiscount === 'none') filters.has_discount = false

  const search = debouncedSearch.trim()
  if (search) filters.search = search

  return filters
}

export function useAllOrders() {
  const [filters, setFilters] = useState<AllOrdersFilterState>(EMPTY_ALL_ORDERS_FILTERS)
  const [unpaidView, setUnpaidViewState] = useState(false)
  const [sortField, setSortField] = useState<AllOrdersSortField>('CREATED_AT')
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC')
  const [offset, setOffset] = useState(0)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const appliedSearchRef = useRef('')

  // Debounce the search box (useDiscountPreview pattern)
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search)
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [filters.search])

  // Reset pagination when the applied search actually changes ('' → '' on
  // mount never resets)
  useEffect(() => {
    if (appliedSearchRef.current !== debouncedSearch) {
      appliedSearchRef.current = debouncedSearch
      setOffset(0)
    }
  }, [debouncedSearch])

  const setFilter = <K extends keyof AllOrdersFilterState>(
    key: K,
    value: AllOrdersFilterState[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    // Search resets via its debounce; everything else resets immediately
    if (key !== 'search') setOffset(0)
  }

  const resetFilters = () => {
    setFilters(EMPTY_ALL_ORDERS_FILTERS)
    setOffset(0)
  }

  const setUnpaidView = (value: boolean) => {
    setUnpaidViewState(value)
    setOffset(0)
  }

  // Same field flips direction; a new field starts at DESC
  const toggleSort = (field: AllOrdersSortField) => {
    if (field === sortField) {
      setSortDirection(prev => (prev === 'DESC' ? 'ASC' : 'DESC'))
    } else {
      setSortField(field)
      setSortDirection('DESC')
    }
    setOffset(0)
  }

  const hasActiveFilters = useMemo(
    () =>
      filters.dateFrom !== '' ||
      filters.dateTo !== '' ||
      filters.statuses.length > 0 ||
      filters.orderType !== '' ||
      filters.sources.length > 0 ||
      filters.minTotal.trim() !== '' ||
      filters.maxTotal.trim() !== '' ||
      filters.hasDiscount !== 'both' ||
      filters.search.trim() !== '',
    [filters]
  )

  const apiFilters = useMemo(
    () => buildAllOrdersFilters(filters, unpaidView, debouncedSearch),
    [filters, unpaidView, debouncedSearch]
  )

  const { data, loading, error, refetch } = useQuery<{ allOrders: AllOrdersPage }>(
    GET_ALL_ORDERS,
    {
      variables: {
        filters: apiFilters,
        limit: ALL_ORDERS_PAGE_SIZE,
        offset,
        sortField,
        sortDirection,
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  )

  const orders: Order[] = data?.allOrders.orders ?? []
  const total = data?.allOrders.total ?? 0

  // If a filter change shrank the result set while we were on a late page,
  // snap back to the last valid page.
  useEffect(() => {
    if (!loading && total > 0 && offset >= total) {
      const lastPageOffset = Math.floor((total - 1) / ALL_ORDERS_PAGE_SIZE) * ALL_ORDERS_PAGE_SIZE
      setOffset(lastPageOffset)
    }
  }, [loading, total, offset])

  const limit = ALL_ORDERS_PAGE_SIZE
  const page = Math.floor(offset / limit) + 1
  const pageCount = Math.max(1, Math.ceil(total / limit))
  const rangeLabel =
    data && total > 0
      ? `Showing ${(offset + 1).toLocaleString('nl-NL')}–${Math.min(offset + limit, total).toLocaleString('nl-NL')} of ${total.toLocaleString('nl-NL')}`
      : ''

  const nextPage = () => setOffset(prev => (prev + limit < total ? prev + limit : prev))
  const prevPage = () => setOffset(prev => Math.max(0, prev - limit))

  return {
    // filter state
    filters,
    setFilter,
    resetFilters,
    hasActiveFilters,
    // unpaid research view
    unpaidView,
    setUnpaidView,
    // sort
    sortField,
    sortDirection,
    toggleSort,
    // pagination
    offset,
    limit,
    page,
    pageCount,
    rangeLabel,
    nextPage,
    prevPage,
    // query result
    orders,
    total,
    loading,
    error,
    refetch,
    // exposed for future report pages
    apiFilters,
  }
}
